import { Button, styled } from "@mui/material";
import {
    List,
    Datagrid,
    TextField,
    TextInput,
    TopToolbar,
    CreateButton,
    useDataProvider,
    useNotify,
    DateField,
    DateInput,
    useListContext,
    useRefresh,
} from "react-admin";
import FileUploadIcon from '@mui/icons-material/FileUpload';
import DownloadIcon from '@mui/icons-material/Download';
import { useState } from "react";
import { ErrorDetailsDialog } from "../../Components/ErrorDetailsDialog"; // 导入新的对话框组件

const postFilters = [
    <TextInput label="设备SN" alwaysOn source="sn" />,
    <TextInput label="订单号" alwaysOn source="orderNo" />,
    <DateInput label="入库开始日期" alwaysOn source="inStockStartDate" />,
    <DateInput label="入库结束日期" alwaysOn source="inStockEndDate" />,
];

const VisuallyHiddenInput = styled('input')({
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    height: 1,
    overflow: 'hidden',
    position: 'absolute',
    bottom: 0,
    left: 0,
    whiteSpace: 'nowrap',
    width: 1,
});

const ListActions = () => {
    const { setFilters, filterValues } = useListContext();  // 获取列表上下文
    const initialFilters = { status: '1' };  // 初始过滤器值
    const refresh = useRefresh(); // 获取刷新函数
    // 重置过滤器函数
    const handleResetFilters = () => {
        setFilters(initialFilters);
    };

    const dataProvider = useDataProvider(); // 获取数据提供者
    const notify = useNotify(); // 获取通知方法
    const [isUploading, setIsUploading] = useState(false); // 上传状态
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [importResult, setImportResult] = useState<{
        successCount: number;
        failCount: number;
        failedRecords: Array<any>;
    } | null>(null);

    // 处理文件上传
    const handleFileUpload = async (event: any) => {
        const file = event.target.files?.[0];
        if (!file) return;

        setIsUploading(true);

        try {
            // 调用批量导入接口
            let { data } = await dataProvider.batchImport('storeProduct', {
                data: {
                    file,
                    // totalCount: 100
                }
            });
            refresh();  // 触发列表刷新

            // 保存导入结果
            setImportResult({
                successCount: data.successCount || 0,
                failCount: data.failCount || 0,
                failedRecords: data.failedRecords || []
            });

            if (data.failCount > 0) {
                setErrorDialogOpen(true);
            } else {
                notify(`导入成功: 共 ${data.successCount} 个`, {
                    type: 'success',
                    autoHideDuration: 3000
                });
            }
        } catch (error: any) {
            notify(`批量导入失败: ${error.message}`, { type: 'error' });
        } finally {
            setIsUploading(false);
            // 重置文件输入，允许重复上传同一文件
            event.target.value = null;
        }
    };

    // 检查是否有用户主动添加的筛选条件
    const hasUserFilters = () => {
        // 排除默认的 status 字段，检查其他字段是否有值
        const userFilterKeys = Object.keys(filterValues).filter(key =>
            key !== 'status' && filterValues[key] != null && filterValues[key] !== ''
        );
        return userFilterKeys.length > 0;
    };

    const handleCloseErrorDialog = () => {
        setErrorDialogOpen(false);
    };


    const templateUrl = "https://iot-edge-dev.oss-cn-heyuan.aliyuncs.com/templates/产品入库模板.xlsx";
    return (
        <>
            <TopToolbar sx={{ alignItems: "center" }}>
                <Button
                    size="small"
                    onClick={handleResetFilters}
                    disabled={!hasUserFilters()}
                >重置搜索</Button>
                <CreateButton label="新增入库" />
                <Button
                    size="small"
                    component="a"
                    href={templateUrl}
                    download="产品入库模板.xlsx"
                    startIcon={<DownloadIcon />}
                    sx={{ ml: 1 }}
                >
                    下载模板
                </Button>
                <Button
                    size="small"
                    component="label"
                    startIcon={<FileUploadIcon />}
                >
                    批量导入
                    <VisuallyHiddenInput
                        type="file"
                        accept=".xlsx, .xls, .csv" // 限制Excel文件类型
                        onChange={handleFileUpload}
                        disabled={isUploading}
                    />
                </Button>
            </TopToolbar>
            <ErrorDetailsDialog
                open={errorDialogOpen}
                onClose={handleCloseErrorDialog}
                failedRecords={importResult?.failedRecords || []}
                successCount={importResult?.successCount || 0}
                failCount={importResult?.failCount || 0}
            />
        </>
    );
};


export const storeProductList = () => {
    return (
        <List filter={{ status: '1' }} filters={postFilters} actions={<ListActions />} empty={false}>
            <Datagrid bulkActionButtons={false}>
                <TextField source="id" label="序号" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="sn" label="设备SN" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="deviceType" label="设备类型" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="productModel" label="产品型号" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="macAddress" label="MAC地址" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="orderNo" label="所属订单号" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <DateField source="inStockTime" label="入库日期" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <DateField source="productionDate" label="生产日期" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
                <TextField source="manufacturerName" label="制造工厂" sortable={false} sx={{
                    whiteSpace: 'nowrap',
                }} />
            </Datagrid>
        </List>
    )
}